import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiUsers, FiTool, FiDollarSign, FiShield, FiCheck, FiTrendingUp, FiCalendar } = FiIcons;

const ResidentialServices = () => {
  const services = [
    {
      icon: FiHome,
      title: 'Single Family Homes',
      description: 'Comprehensive management for detached homes and single-family properties throughout Calgary and Airdrie neighborhoods.',
      features: ['Regular inspections', 'Lawn care coordination', 'Seasonal maintenance', 'Neighborhood monitoring']
    },
    {
      icon: FiUsers,
      title: 'Multi-Family Buildings',
      description: 'Professional management for duplexes, triplexes, and small apartment buildings in Calgary\'s growing communities.',
      features: ['Common area maintenance', 'Utility management', 'Parking enforcement', 'Dispute resolution']
    },
    {
      icon: FiHome,
      title: 'Condominiums',
      description: 'Specialized condo management following Alberta Condominium Act regulations with expert board support.',
      features: ['Board support', 'Reserve fund planning', 'Bylaw enforcement', 'Meeting coordination']
    },
    {
      icon: FiHome,
      title: 'Townhouses',
      description: 'Efficient management for townhome communities and strata properties across Calgary and Airdrie.',
      features: ['Exterior maintenance', 'Roof care', 'Shared facility management', 'Landscaping']
    }
  ];

  const features = [
    {
      icon: FiUsers,
      title: 'Tenant Screening',
      description: 'Thorough background checks, credit verification, and rental history analysis to find quality Calgary tenants.'
    },
    {
      icon: FiDollarSign,
      title: 'Rent Collection',
      description: 'Automated rent collection with online payment options and timely follow-up on late payments.'
    },
    {
      icon: FiTool,
      title: 'Maintenance Coordination',
      description: '24/7 emergency response and scheduled maintenance with our network of trusted Calgary contractors.'
    },
    {
      icon: FiShield,
      title: 'Legal Compliance',
      description: 'Full compliance with Alberta Residential Tenancies Act and Calgary municipal bylaws.'
    },
    {
      icon: FiCalendar,
      title: 'Regular Inspections',
      description: 'Quarterly property inspections with detailed reports and photographic documentation.'
    },
    {
      icon: FiTrendingUp,
      title: 'Market Analysis',
      description: 'Regular Calgary rental market analysis to ensure competitive rates and maximum returns.'
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Residential Property Management Calgary",
    "description": "Professional residential property management services in Calgary and Airdrie. Expert tenant placement, maintenance coordination, and investment optimization.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "DGM Property Management",
      "telephone": "587-800-1215",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Calgary",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      }
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.0447",
        "longitude": "-114.0719"
      },
      "geoRadius": "50000"
    },
    "serviceType": ["Residential Property Management", "Tenant Placement", "Property Maintenance", "Rent Collection"]
  };

  return (
    <>
      <SEOHead
        title="Residential Property Management Calgary | Expert Tenant Services"
        description="DGM Property Management offers expert residential property management in Calgary & Airdrie. Specializing in single family homes, condos, townhouses with 98% tenant satisfaction. Call 587-800-1215."
        keywords="residential property management Calgary, tenant placement Calgary, Calgary rental management, condo management Calgary, house rental management Calgary, Airdrie property management, Alberta residential tenancies"
        canonical="/residential"
        ogImage="https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Residential Property Management Calgary
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Calgary's trusted residential property management company. We maximize your rental income through expert tenant placement, proactive maintenance, and comprehensive property care across Calgary and Airdrie.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Calgary Residential Properties We Manage
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Specialized management solutions for all types of Calgary residential properties with local market expertise.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <div className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center gap-2">
                            <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Comprehensive Calgary Residential Services
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                End-to-end property management solutions designed specifically for Calgary's competitive rental market.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg"
                >
                  <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <SafeIcon icon={feature.icon} className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">
                Maximize Your Calgary Rental Property Investment
              </h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Join hundreds of Calgary property owners who trust DGM for professional residential management. Average ROI increase of 23% in first year.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/#contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Get Free Rental Analysis
                </motion.a>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Call 587-800-1215
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ResidentialServices;